/**
 * One page — its sections, their order, its meta — FR-I2, FR-I14,
 * T-4.33…T-4.35, and [dukaan §3](../../../../../docs/legacy/dukaan.md)'s third
 * step: **fill**. Pages as sections, each section a form. Redrawn in Wave 14C
 * (C13, spec §10.5 item 5; T-4.63) on the edit page and its one edit bar.
 *
 * ## One Save, for the sections and the page's details
 *
 * The bar at the bottom (interface I-1) saves **everything on the screen**:
 * the sections through `PUT /pages/{id}/blocks` and the details — title, menu
 * label, search-engine title and description, *in the menu* — through
 * `PATCH /pages/{id}`, each only when it changed. The sections go first, because
 * they are the ones the service can refuse field by field; a refusal there
 * stops the save before the details are sent, so a firm never has half a save
 * it did not see. Discard puts both back. Leaving with either changed asks.
 *
 * *Add a section* is offered **at the top, between every two sections and at
 * the end**, and the picker says where the new one will go.
 *
 * ## The unit of work is the page
 *
 * `PUT /pages/{id}/blocks` replaces the whole ordered list, and that is the
 * service's decision for a reason this screen makes obvious: a firm reorders
 * three sections and deletes one in a single pass, and a patch protocol would
 * make the browser responsible for describing that correctly. So the editor
 * holds the list, the firm rearranges it, and Save sends what is on the screen.
 *
 * ## Nothing here validates
 *
 * `app/domain/site.py: validate_block` is the only validator (ADR-0013 §4 — no
 * markup, no scripts, on a page that shares an origin with the client portal).
 * When it refuses, it says which block index, which field and why, and
 * `blockIssue` sorts that onto the section that caused it so the editor can
 * open the right one. A second validator in this bundle would be a second
 * answer, and on this module the one the firm sees would be the one letting a
 * script through.
 *
 * The one refusal worth its own sentence is `html_not_allowed`: a firm pasting
 * from Word does not know it has pasted markup, and "that is not allowed here"
 * would leave it stuck.
 *
 * ## And nothing here describes a section
 *
 * Twenty-two sections, and not one field name in this file. The picker's
 * drawings are read off the schema (`SectionShape`) and the forms are drawn
 * from it (`BlockForm`), both out of the one `GET /api/v1/site/vocabulary` —
 * `lib/api/site.ts`'s rule 2. Thirteen sections arrived in 8C and this screen
 * needed no list of them.
 *
 * ## The four things it says that a firm cannot see
 *
 * A section that **reads live data** changes after publishing, and a section
 * that **this firm's template draws in its own markup** will not look like the
 * plain one. Both are true, neither is visible in an editor, and a firm only
 * discovers the first by watching its own website change (T-4.34). The third
 * mark is the one ADR-0014 §3 has described since Wave 3 and nothing could
 * implement: a section the firm's conduct policy keeps off the site, shown with
 * its content and the reason, linking to the switch.
 *
 * The fourth arrived from a real firm (w8h, finding 3): a section with
 * **nothing in the catalogue to draw**. `services` over an empty catalogue
 * renders nothing on the public page, deliberately and rightly, and until this
 * session the only way to find out why was to know that `site_visible` exists.
 */

import { Alert, Badge, Button, Card, CardBody, Field, Input, Skeleton, Switch, Textarea } from '@fiveox/ui'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Copy,
  EyeOff,
  GripVertical,
  LayoutList,
  PackageOpen,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  Undo2,
  Wand2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useParams } from 'react-router'

import { EditBar, EditPage, useDraftPreviewHref, useLeaveGuard } from '../../components/edit'
import { SERVICE, SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { firm } from '../../lib/api/firm'
import { services as catalogue, serviceCatalog, type Service, type ServiceCatalog } from '../../lib/api/services'
import {
  addableBlocks,
  blockIssue,
  isMarkupRefusal,
  site,
  type BlockSchema,
  type Page,
  type Vocabulary,
} from '../../lib/api/site'
import { BlockForm } from './fields/BlockForm'
import { ServiceCatalogProvider } from './fields/ServicePicker'
import { DraftsPanel } from './Drafts'
import { pagePreviewHref } from './Pages'
import { SectionPicker, type PickerContext } from './SectionPicker'
import {
  EMPTY,
  emptyCatalogueNote,
  emptyNote,
  liveDataNote,
  uid,
  resolvedSwitches,
  sectionLabel,
  sectionsReducer,
  suppression,
  themeNote,
  toPayload,
  type EditorBlock,
} from './sections'
import { useSite } from './useSite'

/** Which control on a card the firm was using, so focus can be put back on the
 *  same one after the list moves under it (T-4.35). */
type Control = 'grip' | 'up' | 'down' | 'duplicate'

/* ── the page's details (pure, tested) ───────────────────────────────────── */

/** The page's own fields, as the form holds them — empty strings, not nulls. */
export interface PageDetails {
  title: string
  nav_label: string
  meta_title: string
  meta_description: string
  in_nav: boolean
}

export function detailsOf(page: Pick<Page, 'title' | 'nav_label' | 'meta_title' | 'meta_description' | 'in_nav'>): PageDetails {
  return {
    title: page.title,
    nav_label: page.nav_label ?? '',
    meta_title: page.meta_title ?? '',
    meta_description: page.meta_description ?? '',
    in_nav: page.in_nav,
  }
}

export function detailsChanged(a: PageDetails, b: PageDetails): boolean {
  return (Object.keys(a) as (keyof PageDetails)[]).some((key) => a[key] !== b[key])
}

/** What `PATCH /pages/{id}` is sent: an empty optional field is `null`, which
 *  the service reads as *use the default*. */
export function detailsPatch(details: PageDetails): Partial<Page> {
  return {
    title: details.title.trim(),
    nav_label: details.nav_label.trim() || null,
    meta_title: details.meta_title.trim() || null,
    meta_description: details.meta_description.trim() || null,
    in_nav: details.in_nav,
  }
}

/** Where *Add a section* at `at` will put the new one, in words. */
export function insertionNote(at: number, labels: string[]): string {
  if (labels.length === 0) return 'It will be the first section on this page.'
  if (at <= 0) return 'It goes at the top of the page.'
  if (at >= labels.length) return 'It goes at the end of the page.'
  return `It goes after “${labels[at - 1]}”.`
}

/* ── the screen ──────────────────────────────────────────────────────────── */

export function SitePageEditor() {
  const { pageId = '' } = useParams()
  const { can } = useEvaluation()
  const editable = can(SITE, 'can_update')
  const { vocabulary, site: firmSite } = useSite()
  const draftHref = useDraftPreviewHref()

  const [page, setPage] = useState<Page | null>(null)
  const [state, dispatch] = useReducer(sectionsReducer, EMPTY)
  const [open, setOpen] = useState<string | null>(null)
  /** Where the picker will insert, or `null` while it is closed. */
  const [pickingAt, setPickingAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issue, setIssue] = useState<ReturnType<typeof blockIssue>>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [switches, setSwitches] = useState<Record<string, boolean>>({})
  const [grabbed, setGrabbed] = useState<string | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  // w8h, finding 3. **`null` until it is known, and `null` for ever if it
  // cannot be.** Not `0`: a default of nought would tell every firm that its
  // catalogue was empty for the moment between mount and answer, and would go
  // on telling a role that cannot read the catalogue at all.
  const [visibleServices, setVisibleServices] = useState<number | null>(null)
  // C25 (ADR-0054 §5, T-4.135): the same read, as the service picker's list —
  // the services a visitor could be shown, by category, in the firm's order.
  // `null` (not permitted, not read yet, failed) keeps every slug field's box.
  const [pickable, setPickable] = useState<ServiceCatalog | null>(null)

  const listRef = useRef<HTMLDivElement>(null)
  const restore = useRef<{ uid: string; control: Control } | null>(null)

  /** What is saved, and what is on the screen. */
  const [savedDetails, setSavedDetails] = useState<PageDetails | null>(null)
  const [meta, setMeta] = useState<PageDetails>({
    title: '',
    nav_label: '',
    meta_title: '',
    meta_description: '',
    in_nav: true,
  })

  const load = useCallback(async () => {
    const body = await site.page(pageId)
    setPage(body.data)
    dispatch({ kind: 'load', blocks: body.data.blocks ?? [] })
    const details = detailsOf(body.data)
    setSavedDetails(details)
    setMeta(details)
  }, [pageId])

  useEffect(() => {
    void load().catch(() => setError('That page could not be loaded.'))
  }, [load])

  // The firm's conduct switches, for the "not on your website" mark. Its own
  // read rather than one folded into `useSite`: the policy is the firm's and
  // not the website's, and every other builder screen would pay for a call
  // only this one needs.
  useEffect(() => {
    firm
      .policy()
      .then((body) => setSwitches(resolvedSwitches(body.data.switches)))
      // **Fails closed, silently.** A policy read that did not complete leaves
      // every switch `false`, so a suppressible section is marked hidden rather
      // than shown as live. The other way round would tell a firm its
      // testimonials were published when its regulator forbids them.
      .catch(() => setSwitches({}))
  }, [])

  // w8h, finding 3. How many services a visitor would actually see — the same
  // rule the renderer applies (`domain/site.py`: active **and** `site_visible`),
  // so the builder's note and the page agree.
  //
  // C25 (the Wave 27 close): *active* is now **offered** — an active service
  // in an inactive category is not drawn (ADR-0054 §3) — and the staff list
  // 27D serves is never category-gated, so the service's own `offered` answer
  // is read rather than re-derived. `!== false` keeps a service from before it.
  //
  // Gated on `service:can_view` because a practice manager may hold `site` and
  // not the catalogue, and an unpermitted read would 403 into the `catch` and
  // look exactly like an empty catalogue. Both paths leave it `null`, which is
  // the answer that says nothing at all.
  useEffect(() => {
    if (!can(SERVICE, 'can_view')) return
    let cancelled = false
    catalogue
      .list()
      .then((body) => {
        if (cancelled) return
        const onSite = (body.data.services ?? []).filter(
          (service: Service) => service.site_visible && service.offered !== false,
        )
        setVisibleServices(onSite.length)
        setPickable(serviceCatalog({ services: onSite, categories: body.data.categories ?? [] }))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [can])

  // Focus follows the card, not the position. Deferred a frame because the
  // list re-renders in the same commit that moved it.
  useEffect(() => {
    const target = restore.current
    if (!target) return
    restore.current = null
    const frame = requestAnimationFrame(() => {
      const card = listRef.current?.querySelector<HTMLElement>(`[data-uid="${target.uid}"]`)
      const wanted = card?.querySelector<HTMLButtonElement>(`[data-control="${target.control}"]`)
      // A section moved to the top loses its "Move up" button to `disabled`, so
      // the grip is the floor: focus never lands on nothing.
      const grip = card?.querySelector<HTMLButtonElement>('[data-control="grip"]')
      const landing = wanted && !wanted.disabled ? wanted : grip
      landing?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [state.blocks])

  const schemas = useMemo(() => vocabulary?.blocks ?? [], [vocabulary])
  const schemaFor = useCallback((type: string) => schemas.find((s) => s.type === type), [schemas])
  const labelFor = useCallback((type: string) => sectionLabel(schemaFor(type), type), [schemaFor])

  const template = useMemo(
    () => vocabulary?.templates.find((t) => t.code === firmSite?.template) ?? null,
    [vocabulary, firmSite],
  )
  const context: PickerContext = useMemo(
    () => ({
      templateCode: firmSite?.template ?? null,
      templateName: template?.name ?? null,
      vocabulary,
      switches,
      visibleServices,
    }),
    [firmSite, template, vocabulary, switches, visibleServices],
  )

  const detailsDirty = savedDetails !== null && detailsChanged(meta, savedDetails)
  const dirty = state.dirty || detailsDirty
  const guard = useLeaveGuard(editable && dirty)

  const move = (block: EditorBlock, to: number, control: Control) => {
    restore.current = { uid: block.uid, control }
    dispatch({ kind: 'move', uid: block.uid, to, label: labelFor(block.type) })
  }

  /** Sections first, then details — each only when it changed. */
  const save = async () => {
    setBusy(true)
    setError(null)
    setIssue(null)
    setNotice(null)
    const sectionsChanged = state.dirty
    try {
      if (sectionsChanged) {
        try {
          const body = await site.saveBlocks(pageId, toPayload(state.blocks))
          setPage(body.data)
          dispatch({ kind: 'load', blocks: body.data.blocks ?? [] })
        } catch (err) {
          if (err instanceof ApiError) {
            const found = blockIssue(err.details)
            setIssue(found)
            // The refusal names the block, so open it — an editor with fourteen
            // sections and a message at the top is an editor that makes a person
            // hunt. This is the whole reason the service sends `block_index`.
            const hit = found ? state.blocks[found.index] : null
            if (hit) setOpen(hit.uid)
            setError(
              isMarkupRefusal(err.details)
                ? 'It looks like some of that was pasted from a word processor and carries formatting codes. Your site does not accept those — paste it as plain text and use the headings and lists here instead.'
                : err.message,
            )
          } else {
            setError('That page could not be saved.')
          }
          return
        }
      }
      if (detailsDirty) {
        try {
          const body = await site.updatePage(pageId, detailsPatch(meta))
          // `PATCH` answers without the blocks; the ones on the screen stand.
          setPage((was) => ({ ...body.data, blocks: body.data.blocks ?? was?.blocks }))
          const details = detailsOf(body.data)
          setSavedDetails(details)
          setMeta(details)
        } catch (err) {
          const why = err instanceof ApiError ? err.message : 'The page details could not be saved.'
          setError(sectionsChanged ? `Your sections are saved; the page details are not. ${why}` : why)
          setDetailsOpen(true)
          return
        }
      }
      setNotice('Saved to your draft.')
    } finally {
      setBusy(false)
    }
  }

  const discard = () => {
    if (!page || !savedDetails) return
    dispatch({ kind: 'load', blocks: page.blocks ?? [] })
    setMeta(savedDetails)
    setOpen(null)
    setGrabbed(null)
    setIssue(null)
    setError(null)
    setNotice(null)
  }

  if (!page || !vocabulary) {
    if (error) return <Alert tone="error">{error}</Alert>
    return (
      <div className="mx-auto w-full max-w-5xl space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  const addable = new Set(
    addableBlocks(
      schemas,
      state.blocks.map((b) => b.type),
    ).map((s) => s.type),
  )
  const labels = state.blocks.map((b) => labelFor(b.type))
  const titleProblem = editable && !meta.title.trim() ? 'Give the page a title before saving.' : null

  return (
    // C25: the slug fields of every section below read the catalogue from here.
    <ServiceCatalogProvider value={pickable}>
    <EditPage
      title={page.title}
      back={{ to: '/site/pages', label: 'Your pages' }}
      description={
        <>
          {page.href === '/' ? 'Your home page' : <span className="font-mono">{page.href}</span>} ·
          editing your draft. Nothing on your live site changes until you publish.
        </>
      }
      meta={
        <span className="flex flex-wrap gap-1.5">
          <Badge size="sm" tone={state.blocks.length === 0 ? 'warning' : 'neutral'}>
            {state.blocks.length === 0
              ? 'Empty'
              : `${state.blocks.length} section${state.blocks.length === 1 ? '' : 's'}`}
          </Badge>
          {meta.in_nav ? (
            <Badge size="sm" tone="success">
              In the menu
            </Badge>
          ) : (
            <Badge size="sm">Not in the menu</Badge>
          )}
        </span>
      }
    >
      {guard}

      {error && <Alert tone="error">{error}</Alert>}
      {notice && !dirty && <Alert tone="success">{notice}</Alert>}

      {/* Every reorder, add, duplicate and delete is announced here. Without it
          a keyboard reorder is completely silent: the list moves and a screen
          reader has no reason to re-read it (T-4.35). */}
      <p aria-live="polite" role="status" className="sr-only">
        {state.announcement}
      </p>

      {editable && (
        <DetailsCard
          open={detailsOpen}
          onToggle={() => setDetailsOpen((was) => !was)}
          meta={meta}
          changed={detailsDirty}
          titleProblem={titleProblem}
          onChange={setMeta}
        />
      )}

      {/* ── the sections ──────────────────────────────────────────────── */}
      <section aria-labelledby="page-sections" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="page-sections" className="text-foreground text-base font-semibold">
            Sections
          </h2>
          {state.blocks.length > 0 && (
            <p className="text-muted-foreground text-xs">
              Open a section to fill it in. Move it with the arrows, or drag it by its grip.
            </p>
          )}
        </div>

        {state.removed && (
          <Alert tone="info">
            <span className="flex flex-wrap items-center gap-3">
              <span>{state.removed.label} removed. It is still in your draft until you save.</span>
              <Button size="sm" variant="secondary" onClick={() => dispatch({ kind: 'undo' })}>
                <Undo2 size={14} aria-hidden="true" /> Put it back
              </Button>
            </span>
          </Alert>
        )}

        <div ref={listRef} className="space-y-2">
          {state.blocks.length === 0 ? (
            <div
              data-sections-empty=""
              className="border-border flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center"
            >
              <span className="bg-muted text-primary flex h-12 w-12 items-center justify-center rounded-full">
                <LayoutList size={22} aria-hidden />
              </span>
              <div className="max-w-md space-y-1">
                <p className="text-foreground text-base font-semibold">This page is empty</p>
                <p className="text-muted-foreground text-sm">
                  Add a section — a banner, your services, a few words about the firm — or start
                  from the wording under “Something to start from” below.
                </p>
              </div>
              {editable && (
                <Button onClick={() => setPickingAt(0)}>
                  <Plus size={14} aria-hidden="true" /> Add a section
                </Button>
              )}
            </div>
          ) : (
            <>
              {editable && <InsertSlot at={0} label="Add a section at the top" onPick={setPickingAt} />}
              {state.blocks.map((block, index) => (
                <div key={block.uid} className="space-y-2">
                  <SectionCard
                    block={block}
                    index={index}
                    total={state.blocks.length}
                    schema={schemaFor(block.type)}
                    label={labels[index]!}
                    editable={editable}
                    context={context}
                    vocabulary={vocabulary}
                    expanded={open === block.uid}
                    grabbed={grabbed === block.uid}
                    issue={issue?.index === index ? issue : null}
                    onToggle={() => setOpen(open === block.uid ? null : block.uid)}
                    onGrab={() => setGrabbed(grabbed === block.uid ? null : block.uid)}
                    onMove={(to, control) => move(block, to, control)}
                    onDropSection={(dragged) => {
                      const source = state.blocks.find((b) => b.uid === dragged)
                      if (source) move(source, index, 'grip')
                    }}
                    onDuplicate={() => {
                      restore.current = { uid: block.uid, control: 'duplicate' }
                      dispatch({ kind: 'duplicate', uid: block.uid, label: labels[index]! })
                    }}
                    onRemove={() => {
                      if (open === block.uid) setOpen(null)
                      if (grabbed === block.uid) setGrabbed(null)
                      dispatch({ kind: 'remove', uid: block.uid, label: labels[index]! })
                    }}
                    onChange={(props) => dispatch({ kind: 'edit', uid: block.uid, props })}
                  />
                  {editable && index < state.blocks.length - 1 && (
                    <InsertSlot
                      at={index + 1}
                      label={`Add a section after ${labels[index]}`}
                      onPick={setPickingAt}
                    />
                  )}
                </div>
              ))}
              {editable && (
                <button
                  type="button"
                  data-insert-at={state.blocks.length}
                  onClick={() => setPickingAt(state.blocks.length)}
                  className="border-border text-muted-foreground hover:border-primary hover:text-primary flex w-full items-center justify-center gap-2 rounded-xl border border-dashed p-3 text-sm font-medium transition"
                >
                  <Plus size={14} aria-hidden="true" /> Add a section
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* AI drafts for this page, or the template's placeholder copy — which is
          what every firm gets today (spec §9-4). */}
      <DraftsPanel
        pageId={pageId}
        onUse={(type, props) => {
          if (!schemaFor(type)) return
          const id = uid()
          dispatch({ kind: 'insert', uid: id, type, props, label: labelFor(type) })
          setOpen(id)
        }}
      />

      {editable && (
        <SectionPicker
          open={pickingAt !== null}
          onClose={() => setPickingAt(null)}
          schemas={schemas}
          addable={addable}
          context={context}
          where={pickingAt === null ? undefined : insertionNote(pickingAt, labels)}
          onAdd={(schema) => {
            // Added, and opened: a firm that has just chosen *Metrics* is
            // going to type its figures into it, and an editor that adds a
            // collapsed card where it cannot see it makes it go and find it.
            const id = uid()
            dispatch({
              kind: 'add',
              uid: id,
              schema,
              label: sectionLabel(schema, schema.type),
              at: pickingAt ?? undefined,
            })
            setOpen(id)
          }}
        />
      )}

      <EditBar
        dirty={dirty}
        saving={busy}
        editable={editable}
        onSave={() => void save()}
        onDiscard={discard}
        problem={titleProblem}
        previewHref={pagePreviewHref(draftHref, page.href)}
      />
    </EditPage>
    </ServiceCatalogProvider>
  )
}

/* ── the gap between two sections ────────────────────────────────────────── */

/** *Add a section* in a gap: a hairline with a small pill on it, so twelve of
 *  them read as spacing rather than as twelve more buttons. */
function InsertSlot({ at, label, onPick }: { at: number; label: string; onPick: (at: number) => void }) {
  return (
    <div className="group flex items-center gap-2" data-insert-slot={at}>
      <span className="bg-border h-px flex-1" aria-hidden />
      <button
        type="button"
        data-insert-at={at}
        aria-label={label}
        title={label}
        onClick={() => onPick(at)}
        className="border-border bg-background text-muted-foreground hover:border-primary hover:text-primary focus-visible:ring-ring inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none"
      >
        <Plus size={12} aria-hidden /> Add a section
      </button>
      <span className="bg-border h-px flex-1" aria-hidden />
    </div>
  )
}

/* ── the page's details ──────────────────────────────────────────────────── */

function DetailsCard({
  open,
  onToggle,
  meta,
  changed,
  titleProblem,
  onChange,
}: {
  open: boolean
  onToggle: () => void
  meta: PageDetails
  changed: boolean
  titleProblem: string | null
  onChange: (next: PageDetails) => void
}) {
  const summary = [
    meta.nav_label.trim() ? `Menu label “${meta.nav_label.trim()}”` : 'Menu label from the title',
    meta.in_nav ? 'in the menu' : 'not in the menu',
    meta.meta_title.trim() || meta.meta_description.trim() ? 'search text set' : 'search text automatic',
  ].join(' · ')

  return (
    <Card data-page-details="">
      <CardBody className="space-y-4">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="page-details-body"
          onClick={onToggle}
          className="flex w-full items-center gap-3 text-left"
        >
          <span className="bg-muted text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
            <Settings2 size={16} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-foreground flex items-center gap-2 text-base font-semibold">
              Page details
              {changed && (
                <Badge size="sm" tone="info">
                  Changed
                </Badge>
              )}
            </span>
            <span className="text-muted-foreground line-clamp-2 block text-sm">
              {open ? 'Its title, its place in the menu and what search engines show.' : summary}
            </span>
          </span>
          {open ? (
            <ChevronDown size={18} className="text-muted-foreground shrink-0" aria-hidden />
          ) : (
            <ChevronRight size={18} className="text-muted-foreground shrink-0" aria-hidden />
          )}
        </button>

        {open && (
          <div id="page-details-body" className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Title" error={titleProblem}>
                {(control) => (
                  <Input
                    {...control}
                    value={meta.title}
                    maxLength={200}
                    onChange={(e) => onChange({ ...meta, title: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Menu label" hint="Leave empty to use the title.">
                {(control) => (
                  <Input
                    {...control}
                    value={meta.nav_label}
                    maxLength={80}
                    onChange={(e) => onChange({ ...meta, nav_label: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label="Search-engine title"
                hint="Leave empty and we use your page title and your firm's name."
              >
                {(control) => (
                  <Input
                    {...control}
                    value={meta.meta_title}
                    maxLength={200}
                    onChange={(e) => onChange({ ...meta, meta_title: e.target.value })}
                  />
                )}
              </Field>
              <Field
                label="Search-engine description"
                hint="One or two sentences. Leave empty and we use your firm's tagline."
              >
                {(control) => (
                  <Textarea
                    {...control}
                    rows={2}
                    value={meta.meta_description}
                    maxLength={400}
                    onChange={(e) => onChange({ ...meta, meta_description: e.target.value })}
                  />
                )}
              </Field>
            </div>
            <Switch
              label="Show this page in the menu"
              checked={meta.in_nav}
              onChange={(e) => onChange({ ...meta, in_nav: e.target.checked })}
            />
            <p className="text-muted-foreground text-xs">
              These are saved with the page’s sections, from the bar at the bottom.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * One section on the page.
 *
 * Exported because it is what the tests mount: the editor above it needs a
 * page, a vocabulary, a policy read and a router, and none of that is what
 * T-4.33's permission clause or T-4.35's keyboard clause is about.
 */
export function SectionCard({
  block,
  index,
  total,
  schema,
  label,
  editable,
  context,
  vocabulary,
  expanded,
  grabbed,
  issue,
  onToggle,
  onGrab,
  onMove,
  onDropSection,
  onDuplicate,
  onRemove,
  onChange,
}: {
  block: EditorBlock
  index: number
  total: number
  schema: BlockSchema | undefined
  label: string
  /** `site:can_update`. Without it the page is readable and nothing moves. */
  editable: boolean
  context: PickerContext
  vocabulary: Vocabulary | null
  expanded: boolean
  grabbed: boolean
  issue: { field: string | null; reason: string | null } | null
  onToggle: () => void
  onGrab: () => void
  onMove: (to: number, control: Control) => void
  /** A section dropped onto this one — **that** one moves, to this index. */
  onDropSection: (draggedUid: string) => void
  onDuplicate: () => void
  onRemove: () => void
  onChange: (props: Record<string, unknown>) => void
}) {
  const theme = themeNote(context.templateCode, context.templateName, block.type)
  const live = liveDataNote(block.type)
  const hidden = suppression(block.type, vocabulary, context.switches)
  const empty = emptyCatalogueNote(block.type, context.visibleServices)
  // C24 item 11 — the owner's finding: a section added, filled in, and absent
  // from the preview with nothing saying so. Asked of the renderer's own
  // reader (`whyHidden`), and only when neither of the two older notices is
  // already saying something about this section.
  const unfinished = hidden || empty
    ? null
    : emptyNote(block.type, block.props, context.switches, context.visibleServices)

  /**
   * The keyboard's drag (T-4.35), on the grip the mouse drags.
   *
   * `aria-pressed` is what says the section is held: pick it up with Enter or
   * Space, move it with the arrows, put it down with either again or with
   * Escape. Focus stays on the grip the whole way, which is what makes it a
   * drag rather than four separate presses — and the live region above the list
   * says where it landed after every press.
   */
  const onGripKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onGrab()
      return
    }
    if (!grabbed) return
    if (e.key === 'Escape') {
      e.preventDefault()
      onGrab()
      return
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      onMove(index + (e.key === 'ArrowUp' ? -1 : 1), 'grip')
    }
  }

  return (
    <Card
      data-uid={block.uid}
      className={[
        issue ? 'border-destructive' : '',
        grabbed ? 'ring-2 ring-primary' : '',
        hidden || unfinished ? 'border-dashed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // Draggable only while collapsed: an open section is a form, and a form
      // whose card is `draggable` cannot have its text selected with a mouse.
      draggable={editable && !expanded}
      onDragStart={(e) => e.dataTransfer.setData('text/prodesk-section', block.uid)}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('text/prodesk-section')) e.preventDefault()
      }}
      onDrop={(e) => {
        const dragged = e.dataTransfer.getData('text/prodesk-section')
        if (!dragged || dragged === block.uid) return
        e.preventDefault()
        // **The section that moves is the one being dragged**, to this one's
        // place, and the reducer closes the gap behind it. Moving *this* card
        // to its own index is a no-op, which is what the first cut of this did
        // and what nothing but reading it would have caught — a drag that
        // appears to work and changes nothing.
        //
        // It is the same `move` the arrows and the buttons dispatch, so there
        // is one ordering rule and one set of tests over it.
        onDropSection(dragged)
      }}
    >
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-start gap-2">
          {editable && (
            <button
              type="button"
              data-control="grip"
              data-uid-grip={block.uid}
              aria-pressed={grabbed}
              aria-label={
                grabbed
                  ? `${label}, held at ${index + 1} of ${total}. Arrow keys move it, Enter puts it down.`
                  : `Reorder ${label}, ${index + 1} of ${total}. Press Enter to pick it up.`
              }
              onClick={onGrab}
              onKeyDown={onGripKey}
              className="mt-0.5 cursor-grab rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <GripVertical size={16} aria-hidden="true" />
            </button>
          )}

          <button
            type="button"
            className="flex min-w-[10rem] flex-1 items-center gap-2 py-1 text-left"
            aria-expanded={expanded}
            onClick={onToggle}
          >
            {expanded ? (
              <ChevronDown size={16} className="shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRight size={16} className="shrink-0" aria-hidden="true" />
            )}
            <span
              aria-hidden="true"
              className="bg-muted text-muted-foreground flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md px-1 text-xs font-semibold"
            >
              {index + 1}
            </span>
            <span className="shrink-0 text-sm font-medium text-foreground">{label}</span>
            <span className="min-w-0 truncate text-xs text-muted-foreground">{summarise(block)}</span>
          </button>

          {editable && (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                data-control="up"
                aria-label={`Move ${label} up`}
                disabled={index === 0}
                onClick={() => onMove(index - 1, 'up')}
              >
                <ArrowUp size={14} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-control="down"
                aria-label={`Move ${label} down`}
                disabled={index === total - 1}
                onClick={() => onMove(index + 1, 'down')}
              >
                <ArrowDown size={14} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-control="duplicate"
                aria-label={`Duplicate ${label}`}
                onClick={onDuplicate}
              >
                <Copy size={14} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-control="remove"
                aria-label={`Remove ${label}`}
                onClick={onRemove}
              >
                <Trash2 size={14} aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>

        {(theme || live || hidden || empty) && (
          <div className="space-y-1.5">
            {/* w8h, finding 3. Above the live-data note and for the same
                reason: a firm reading *"reads your service catalogue"* on a
                section that draws nothing needs the next sentence to be why. */}
            {empty && (
              <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge tone="warning">
                  <PackageOpen size={11} aria-hidden="true" /> Nothing to show yet
                </Badge>
                <span>{empty}</span>
                <Link to="/services" className="underline">
                  Your services
                </Link>
              </p>
            )}
            {hidden && (
              <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge tone="warning">
                  <EyeOff size={11} aria-hidden="true" /> Not on your website
                </Badge>
                <span>{hidden.reason}</span>
                <Link to="/site/policy" className="underline">
                  Your conduct policy
                </Link>
              </p>
            )}
            {/* C24 item 11. No link: what is missing is in this section's own
                form, which is the next thing under this line. */}
            {unfinished && (
              <p
                className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
                data-unfinished={block.type}
              >
                <Badge tone="warning">
                  <EyeOff size={11} aria-hidden="true" /> Not on your website yet
                </Badge>
                <span>{unfinished}</span>
              </p>
            )}
            {live && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <RefreshCw size={12} aria-hidden="true" className="mt-0.5 shrink-0" />
                {live}
              </p>
            )}
            {theme && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Wand2 size={12} aria-hidden="true" className="mt-0.5 shrink-0" />
                {theme}
              </p>
            )}
          </div>
        )}

        {expanded && schema && (
          <>
            <p className="text-xs text-muted-foreground">{schema.description}</p>
            <BlockForm schema={schema} value={block.props} issue={issue} onChange={onChange} />
          </>
        )}
      </CardBody>
    </Card>
  )
}

/** A collapsed section's one-line summary — the heading if it has one, so a
 *  firm scanning eight collapsed sections can tell them apart. */
function summarise(block: EditorBlock): string {
  const heading = block.props.heading ?? block.props.headline ?? block.props.alt
  return typeof heading === 'string' && heading.trim() ? `— ${heading.trim().slice(0, 60)}` : ''
}
